import { useMemo } from 'react';

import { DateRangePicker, Input, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { SearchFilter } from '~/components/common';

import { getAllTemperatureOptions } from '~/utils';

import { OPTION_ALL, RETURN_DISPOSAL_STATUS_BY_METHOD, type TypeReturnDisposalProcessMethod } from '~/constants';

import type {
  TypeReturnDisposalFilter,
  TypeReturnDisposalFilterOptions,
  TypeReturnDisposalFilterStatus,
} from '~/types/receive.types';

import styles from './ReceiveReturnDisposalSearchFilter.module.scss';

/**
 * [입고관리 > 회송/폐기 관리] 조회 필터
 *
 * @note 필터 옵션은 서버 제공값을 사용한다. 입고센터(1depth) 선택 시 입고창고(2depth) 옵션이 종속 갱신된다.
 */
const ReceiveReturnDisposalSearchFilter = ({
  options,
  values,
  valuesState,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options?: TypeReturnDisposalFilterOptions;
  values: TypeReturnDisposalFilter;
  valuesState: TypeReturnDisposalFilterStatus;
  onChange: (values: Partial<TypeReturnDisposalFilter>) => void;
  onChangeStatus: (values: Partial<TypeReturnDisposalFilterStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  const optionWarehouse = useMemo<TypeSelectOption<string>[]>(() => {
    const grouped = options?.groupedWarehouseByCenter ?? {};
    return values.center.value === OPTION_ALL.value
      ? getAllTemperatureOptions(grouped)
      : (grouped[values.center.value] ?? []);
  }, [options, values.center]);

  /** 처리방법 선택 시 매핑된 처리상태만 노출(전체는 유지). 미선택 시 서버 전체 상태 노출. */
  const optionProcessStatus = useMemo<TypeSelectOption<string>[]>(() => {
    const all = options?.processStatus ?? [];
    const method = values.processMethod?.value;
    if (!method || method === OPTION_ALL.value) return all;

    const allowed = new Set<string>(RETURN_DISPOSAL_STATUS_BY_METHOD[method as TypeReturnDisposalProcessMethod] ?? []);
    return all.filter((option) => option.value === OPTION_ALL.value || allowed.has(option.value));
  }, [options?.processStatus, values.processMethod]);

  return (
    <SearchFilter formId="return-disposal-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text weight="semi-bold" size="small" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            placeholder="선택"
            options={options?.keywordType ?? []}
            value={values.keywordType}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
            }}
          />
          <Input
            type="text"
            placeholder="입력"
            size="small"
            value={values.keyword}
            maxLength={200}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
          />
        </div>
      </div>

      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text weight="semi-bold" size="small" element="p">
          기간
        </Text>
        <div className={styles['input-container']}>
          <Select
            placeholder="선택"
            options={options?.dateType ?? []}
            value={values.dateType}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.dateType?.value) return;
              onChange({ dateType: value });
            }}
          />
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            status={valuesState.dateRange}
            value={values.dateRange}
            onChange={(value) => {
              onChange({ dateRange: value });
              onChangeStatus({ dateRange: 'default' });
            }}
            onFocus={() => onChangeStatus({ dateRange: 'default' })}
          />
        </div>
      </div>

      <div className={clsx(styles['filter-container'], styles['filter-center'])}>
        <Text weight="semi-bold" size="small" element="p">
          입고 센터
        </Text>
        <div className={styles['input-container']}>
          <Select
            placeholder="선택"
            options={options?.center ?? []}
            value={values.center}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.center?.value) return;
              onChange({ center: value, warehouse: OPTION_ALL });
            }}
          />
          <Select
            placeholder="선택"
            disabled={values.center.value === OPTION_ALL.value}
            options={optionWarehouse}
            value={values.warehouse}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.warehouse?.value) return;
              onChange({ warehouse: value });
            }}
          />
        </div>
      </div>

      <div className={clsx(styles['filter-container'], styles['filter-release-code'])}>
        <Text weight="semi-bold" size="small" element="p">
          입고 방법
        </Text>
        <Select
          placeholder="선택"
          options={options?.releaseCode ?? []}
          value={values.releaseCode}
          size="small"
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.releaseCode?.value) return;
            onChange({ releaseCode: value });
          }}
        />
      </div>

      <div className={clsx(styles['filter-container'], styles['filter-process-method'])}>
        <Text weight="semi-bold" size="small" element="p">
          처리 방법
        </Text>
        <Select
          placeholder="선택"
          options={options?.processMethod ?? []}
          value={values.processMethod}
          size="small"
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.processMethod?.value) return;
            onChange({ processMethod: value, processStatus: OPTION_ALL });
          }}
        />
      </div>

      <div className={clsx(styles['filter-container'], styles['filter-process-status'])}>
        <Text weight="semi-bold" size="small" element="p">
          처리 상태
        </Text>
        <Select
          placeholder="선택"
          options={optionProcessStatus}
          value={values.processStatus}
          size="small"
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.processStatus?.value) return;
            onChange({ processStatus: value });
          }}
        />
      </div>
    </SearchFilter>
  );
};

export default ReceiveReturnDisposalSearchFilter;
